import * as Dialog from '@radix-ui/react-dialog';
import { ArrowLeft, Plus, Search, Sparkles, TriangleAlert, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  highlightMatch,
  SearchCombobox,
  type ComboboxAction,
  type ComboboxGroup,
  type ComboboxItem,
} from '@/components/ui/searchable-select';
import { useNarrow } from '@/components/list/use-narrow';
import { cn } from '@/lib/utils';
import { Badge } from '../../list/expense-labels';
import { Notice } from '../page/Notice';
import { SupplierPanel } from '../page/SupplierPanel';
import { getSupplier, searchSupplierPicks, type Supplier, type SupplierPick } from './api';
import {
  CreateSupplierDialog,
  type CreateInitial,
  type CreateResult,
} from './CreateSupplierDialog';
import { DocumentSupplierNotice } from './DocumentSupplierNotice';
import { groupPicks, normalizeVat, pickByVat } from './groups';
import { CategoryIcon } from './icons';
import { useServerSearch } from './use-supplier-search';

/** The expense's supplier as the form holds it. */
export type SupplierValue = {
  readonly supplierId: string;
  readonly supplierName: string;
  readonly supplierVat: string;
};

/** A choice: the supplier and, when the window added or named one, its site. */
export type SupplierChoice = SupplierValue & { readonly siteId: string | null };

const CREATE = 'create';

// ---------------------------------------------------------------------------------------------
// The chosen supplier's page data (sites, marks, uses), read once per id.

export type ChosenSupplier =
  | { readonly status: 'none' }
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly supplier: Supplier }
  | { readonly status: 'error' };

/**
 * The chosen supplier, read with `getSupplier`; a supplier the window just created or completed is
 * adopted as it came back, without reading it again.
 */
export function useChosenSupplier(supplierId: string) {
  const [cache, setCache] = useState<Readonly<Record<string, Supplier>>>({});
  const [failed, setFailed] = useState<string | null>(null);
  const adopt = useCallback(
    (supplier: Supplier) => setCache((prev) => ({ ...prev, [supplier.id]: supplier })),
    [],
  );
  const known = supplierId !== '' ? cache[supplierId] : undefined;
  useEffect(() => {
    if (supplierId === '' || known) return;
    let active = true;
    setFailed(null);
    void getSupplier(supplierId).then((outcome) => {
      if (!active) return;
      if (outcome.ok) adopt(outcome.value);
      else setFailed(supplierId);
    });
    return () => {
      active = false;
    };
  }, [supplierId, known, adopt]);
  let state: ChosenSupplier;
  if (supplierId === '') state = { status: 'none' };
  else if (known) state = { status: 'ready', supplier: known };
  else if (failed === supplierId) state = { status: 'error' };
  else state = { status: 'loading' };
  return { state, adopt } as const;
}

// ---------------------------------------------------------------------------------------------
// Rows

function usePickRows(query: string) {
  const { t } = useTranslation('suppliers');
  return useCallback(
    (pick: SupplierPick): ComboboxItem => {
      // A release-1 server answers only id, name and VAT (contract `searchSuppliers`): every other
      // part of the row is read as possibly absent.
      const alt = Boolean(pick.matchedName) && pick.matchedName !== pick.name;
      const where = [
        alt ? t('picker.matchedName', { name: pick.name }) : null,
        !alt && pick.legalName && pick.legalName !== pick.name ? pick.legalName : null,
        pick.city,
        pick.type ? t(`type.${pick.type}` as 'type.other') : null,
      ].filter(Boolean);
      return {
        id: pick.id,
        leading: <CategoryIcon type={pick.type} />,
        primary: highlightMatch(alt ? pick.matchedName! : pick.name, query),
        secondary: where.length > 0 ? where.join(' · ') : undefined,
        trailing: (
          <>
            {pick.completeness === 'to_complete' && (
              <Badge tone="warning">{t('marks.toComplete')}</Badge>
            )}
            {pick.intermediary && <Badge tone="info">{t('marks.intermediary')}</Badge>}
            {typeof pick.uses === 'number' && (
              <span className="tabular-nums">{t('picker.uses', { count: pick.uses })}</span>
            )}
          </>
        ),
      };
    },
    [t, query],
  );
}

function choiceOfPick(pick: SupplierPick): SupplierChoice {
  return {
    supplierId: pick.id,
    supplierName: pick.name,
    supplierVat: pick.vat ?? '',
    siteId: null,
  };
}

function choiceOfSupplier(supplier: Supplier, siteId: string | null): SupplierChoice {
  return {
    supplierId: supplier.id,
    supplierName: supplier.name,
    supplierVat: supplier.vat ?? '',
    siteId,
  };
}

// ---------------------------------------------------------------------------------------------
// The search: inline under the field on wider screens, a full page on the phone

function SupplierSearch({
  id,
  text,
  onText,
  onChoose,
  onCreate,
  inline,
  describedBy,
  invalid,
  onBlur,
}: {
  readonly id: string;
  readonly text: string;
  readonly onText: (text: string) => void;
  readonly onChoose: (pick: SupplierPick) => void;
  readonly onCreate: (name: string) => void;
  readonly inline: boolean;
  readonly describedBy?: string | undefined;
  readonly invalid?: boolean | undefined;
  readonly onBlur?: (() => void) | undefined;
}) {
  const { t } = useTranslation('suppliers');
  // Only what the person types searches; a name the document proposed does not.
  const [typed, setTyped] = useState<string | null>(null);
  const query = typed ?? '';
  const search = useServerSearch(query, searchSupplierPicks);
  const row = usePickRows(query);
  const groups = useMemo<ComboboxGroup[]>(() => {
    const split = groupPicks(search.items);
    return [
      { id: 'mine', label: t('picker.groups.mine'), items: split.mine.map(row) },
      { id: 'most', label: t('picker.groups.mostUsed'), items: split.mostUsed.map(row) },
      { id: 'others', label: t('picker.groups.others'), items: split.others.map(row) },
    ];
  }, [search.items, row, t]);
  const name = query.trim();
  const down = search.status === 'error';
  const actions = useMemo<ComboboxAction[]>(
    () =>
      name.length >= 2
        ? [
            {
              id: CREATE,
              leading: <Plus className="size-4" />,
              primary: down
                ? t('picker.searchDown.create', { name })
                : t('picker.createRow', { name }),
              secondary: down ? t('picker.searchDown.createHint') : t('picker.createHint'),
            },
          ]
        : [],
    [name, down, t],
  );
  return (
    <SearchCombobox
      id={id}
      query={text}
      onQueryChange={(next) => {
        setTyped(next);
        onText(next);
      }}
      groups={groups}
      actions={actions}
      status={search.status}
      onSelect={(pickId) => {
        const pick = search.items.find((item) => item.id === pickId);
        if (pick) {
          setTyped(null);
          onChoose(pick);
        }
      }}
      onAction={() => onCreate(name)}
      listLabel={t('picker.listLabel')}
      searchingLabel={t('picker.searching')}
      emptyLabel={t('picker.empty')}
      placeholder={t('picker.placeholder')}
      leading={<Search className="size-4" />}
      inline={inline}
      autoFocus={inline}
      onClear={() => {
        setTyped('');
        onText('');
      }}
      clearLabel={t('picker.clear')}
      aria-describedby={describedBy}
      aria-invalid={invalid}
      {...(onBlur ? { onBlur } : {})}
      errorSlot={
        <div className="p-2">
          <Notice
            tone="error"
            icon={TriangleAlert}
            title={t('picker.searchDown.title')}
            actions={
              <Button
                type="button"
                variant="outline"
                className="min-h-11"
                onMouseDown={(event) => event.preventDefault()}
                onClick={search.retry}
              >
                {t('picker.searchDown.retry')}
              </Button>
            }
          >
            <span>{t('picker.searchDown.text')}</span>
          </Notice>
        </div>
      }
      footer={
        inline ? undefined : (
          <>
            <span>{t('picker.footer')}</span>
            <span aria-hidden="true">{t('picker.keys')}</span>
          </>
        )
      }
    />
  );
}

/** Board 1.10, 1.13: on the phone the field opens a full page, the list above the keyboard. */
function PhoneSearchPage({
  id,
  text,
  onText,
  onChoose,
  onCreate,
  onClose,
}: {
  readonly id: string;
  readonly text: string;
  readonly onText: (text: string) => void;
  readonly onChoose: (pick: SupplierPick) => void;
  readonly onCreate: (name: string) => void;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation('suppliers');
  return (
    <Dialog.Root open onOpenChange={(open) => (open ? undefined : onClose())}>
      <Dialog.Portal>
        <Dialog.Content
          aria-describedby={undefined}
          className="bg-card text-foreground fixed inset-0 z-50 flex flex-col"
        >
          <header className="border-border flex items-center gap-2 border-b p-2">
            <Dialog.Close asChild>
              <Button
                type="button"
                variant="ghost"
                className="size-11 p-0"
                aria-label={t('picker.back')}
              >
                <ArrowLeft aria-hidden="true" />
              </Button>
            </Dialog.Close>
            <Dialog.Title className="text-lg font-semibold">{t('picker.openPage')}</Dialog.Title>
          </header>
          <div className="flex min-h-0 flex-1 flex-col p-3">
            <SupplierSearch
              id={id}
              text={text}
              onText={onText}
              onChoose={onChoose}
              onCreate={onCreate}
              inline
              describedBy={undefined}
            />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ---------------------------------------------------------------------------------------------
// The chosen supplier

function ChosenCard({
  id,
  value,
  chosen,
  fromDocument,
  onChange,
  onOpen,
  nameRef,
}: {
  readonly id: string;
  readonly value: SupplierValue;
  readonly chosen: ChosenSupplier;
  readonly fromDocument: boolean;
  readonly onChange: () => void;
  /** The name opens the supplier beside the form (U1). */
  readonly onOpen: () => void;
  readonly nameRef: React.Ref<HTMLButtonElement>;
}) {
  const { t } = useTranslation('suppliers');
  const supplier = chosen.status === 'ready' ? chosen.supplier : null;
  const sites = supplier?.sites.filter((site) => site.active) ?? [];
  const city =
    supplier?.registeredOffice?.address.city ?? sites.find((s) => s.address.city)?.address.city;
  const sub = supplier
    ? [
        supplier.legalName && supplier.legalName !== supplier.name ? supplier.legalName : null,
        city,
        sites.length > 1 ? t('picker.sites', { count: sites.length }) : null,
        t('picker.uses', { count: supplier.uses }),
      ]
        .filter(Boolean)
        .join(' · ')
    : chosen.status === 'loading'
      ? t('picker.loading')
      : value.supplierVat;
  const typed = supplier?.sites.find((s) => s.active && s.macro);
  const type = typed?.detail ?? typed?.macro ?? supplier?.macroCategories[0] ?? null;
  return (
    <div className="flex flex-col gap-1">
      <div
        id={id}
        role="group"
        aria-label={value.supplierName}
        className="border-border bg-card flex min-h-11 items-center gap-3 rounded-md border py-1.5 pr-1 pl-3"
      >
        <span aria-hidden="true" className="text-muted-foreground">
          <CategoryIcon type={type} />
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          {/* Wraps rather than cuts: on the phone the company name, city and uses all show (1.12). */}
          {/* A 44 px target on the phone (U16); the row's own height on wider screens. */}
          <button
            ref={nameRef}
            type="button"
            onClick={onOpen}
            aria-haspopup="dialog"
            className="text-link min-h-11 w-fit text-left text-sm font-bold [overflow-wrap:anywhere] hover:underline @min-[620px]/shell:min-h-0"
          >
            {value.supplierName}
          </button>
          {sub && (
            <small className="text-muted-foreground text-xs [overflow-wrap:anywhere]">{sub}</small>
          )}
          {/* The marks under the text, so the name and the city keep the row's width. */}
          {(supplier?.completeness === 'to_complete' || supplier?.intermediary) && (
            <span className="mt-1 flex flex-wrap gap-1.5">
              {supplier.completeness === 'to_complete' && (
                <Badge tone="warning">{t('marks.toComplete')}</Badge>
              )}
              {supplier.intermediary && <Badge tone="info">{t('marks.intermediary')}</Badge>}
            </span>
          )}
        </span>
        <Button
          type="button"
          variant="ghost"
          className="size-11 shrink-0 p-0"
          aria-label={t('picker.change')}
          onClick={onChange}
        >
          <X aria-hidden="true" />
        </Button>
      </div>
      {fromDocument && (
        <small className="text-muted-foreground flex items-center gap-1 text-xs">
          <Sparkles aria-hidden="true" className="text-brand size-3.5" />
          {t('picker.foundByVat')}
          {value.supplierVat ? ` · ${value.supplierVat}` : ''}
        </small>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// The field

/** The document's VAT lookup (boards 1.3, 1.4): found, not found, or not asked. */
type DocumentLookup = {
  readonly vat: string;
  readonly result: 'found' | 'not-found' | 'dismissed';
} | null;

function useDocumentLookup(
  value: SupplierValue,
  documentVat: string | null,
  choose: (choice: SupplierChoice, fromDocument: boolean) => void,
) {
  const [lookup, setLookup] = useState<DocumentLookup>(null);
  const chooseRef = useRef(choose);
  chooseRef.current = choose;
  const vat = documentVat ? normalizeVat(documentVat) : '';
  // Only while the form still holds the document's number and nothing is chosen.
  const applies =
    vat.length >= 2 && value.supplierId === '' && normalizeVat(value.supplierVat) === vat;
  const asked = lookup?.vat === vat;
  useEffect(() => {
    if (!applies || asked) return;
    let active = true;
    void searchSupplierPicks(vat).then((outcome) => {
      if (!active) return;
      const pick = outcome.ok ? pickByVat(outcome.value, vat) : null;
      // A search that did not answer says nothing either way: no notice.
      if (!outcome.ok) {
        setLookup({ vat, result: 'dismissed' });
        return;
      }
      setLookup({ vat, result: pick ? 'found' : 'not-found' });
      if (pick) chooseRef.current(choiceOfPick(pick), true);
    });
    return () => {
      active = false;
    };
  }, [applies, asked, vat]);
  const notFound = applies && lookup?.vat === vat && lookup.result === 'not-found';
  const dismiss = () => setLookup({ vat, result: 'dismissed' });
  return { notFound, dismiss };
}

/**
 * «Fornitore» in the expense form (boards 1.1–1.13; report §3.2): type and search, the caller's
 * recents first, then the most used, then the rest, then «Crea il fornitore «…»»; or the supplier
 * the document's VAT number found. Free text goes away (§8.2): what is typed and left unchosen is
 * still sent as a name, and the server finds it or creates it «da completare».
 */
export function SupplierPicker({
  id,
  value,
  chosen,
  documentVat,
  onType,
  onChoose,
  onAdopt,
  onRefresh,
  onClear,
  describedBy,
  invalid,
  onBlur,
}: {
  readonly id: string;
  readonly value: SupplierValue;
  readonly chosen: ChosenSupplier;
  /** The VAT number the document proposed, if any. */
  readonly documentVat: string | null;
  readonly onType: (text: string) => void;
  readonly onChoose: (choice: SupplierChoice) => void;
  /** A supplier the window created or completed, to show without reading it again. */
  readonly onAdopt: (supplier: Supplier) => void;
  /**
   * The chosen supplier, changed in its side panel: its new name and VAT number, for the form to
   * show and send. The site the form holds stays as it is.
   */
  readonly onRefresh?: (value: SupplierValue) => void;
  readonly onClear: () => void;
  readonly describedBy?: string | undefined;
  readonly invalid?: boolean | undefined;
  readonly onBlur?: () => void;
}) {
  const { t } = useTranslation('suppliers');
  const narrow = useNarrow();
  const [creating, setCreating] = useState<CreateInitial | null>(null);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [fromDocument, setFromDocument] = useState<string | null>(null);
  const [viewing, setViewing] = useState(false);
  const nameRef = useRef<HTMLButtonElement>(null);
  const choose = (choice: SupplierChoice, byDocument = false): void => {
    setFromDocument(byDocument ? choice.supplierId : null);
    setPhoneOpen(false);
    onChoose(choice);
  };
  const { notFound, dismiss } = useDocumentLookup(value, documentVat, choose);

  const onCreated = (result: CreateResult): void => {
    setCreating(null);
    if (result.kind === 'pick') {
      choose(choiceOfPick(result.pick));
    } else {
      onAdopt(result.supplier);
      choose(choiceOfSupplier(result.supplier, result.siteId));
    }
  };
  const dialog = creating && (
    <CreateSupplierDialog initial={creating} onDone={onCreated} onClose={() => setCreating(null)} />
  );

  if (value.supplierId !== '') {
    return (
      <>
        <ChosenCard
          id={id}
          value={value}
          chosen={chosen}
          fromDocument={fromDocument === value.supplierId}
          onChange={onClear}
          onOpen={() => setViewing(true)}
          nameRef={nameRef}
        />
        {viewing && (
          <SupplierPanel
            supplierId={value.supplierId}
            name={value.supplierName}
            onClose={() => setViewing(false)}
            returnFocus={() => nameRef.current?.focus()}
            onChanged={(supplier) => {
              onAdopt(supplier);
              onRefresh?.({
                supplierId: supplier.id,
                supplierName: supplier.name,
                supplierVat: supplier.vat ?? '',
              });
            }}
          />
        )}
      </>
    );
  }

  if (notFound) {
    return (
      <>
        <DocumentSupplierNotice
          name={value.supplierName}
          vat={value.supplierVat}
          onCreate={() => setCreating({ name: value.supplierName, vat: value.supplierVat })}
          onSearch={dismiss}
        />
        {dialog}
      </>
    );
  }

  const openCreate = (name: string): void => {
    setPhoneOpen(false);
    // The document's number goes with its name only: a typed name starts without one.
    setCreating({ name });
  };

  if (narrow) {
    return (
      <>
        <button
          type="button"
          id={id}
          aria-haspopup="dialog"
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          onClick={() => setPhoneOpen(true)}
          className={cn(
            'border-input bg-card flex min-h-11 w-full items-center gap-2 rounded-md border px-3 text-left text-base',
            value.supplierName === '' && 'text-muted-foreground',
          )}
        >
          <Search aria-hidden="true" className="text-muted-foreground size-4 shrink-0" />
          <span className="truncate">{value.supplierName || t('picker.placeholder')}</span>
        </button>
        {phoneOpen && (
          <PhoneSearchPage
            id={`${id}-page`}
            text={value.supplierName}
            onText={onType}
            onChoose={(pick) => choose(choiceOfPick(pick))}
            onCreate={openCreate}
            onClose={() => setPhoneOpen(false)}
          />
        )}
        {dialog}
      </>
    );
  }

  return (
    <>
      <SupplierSearch
        id={id}
        text={value.supplierName}
        onText={onType}
        onChoose={(pick) => choose(choiceOfPick(pick))}
        onCreate={openCreate}
        inline={false}
        describedBy={describedBy}
        invalid={invalid}
        onBlur={onBlur}
      />
      {dialog}
    </>
  );
}
